import * as preact from "preact";

import * as modalDialog from "modalDialog";
import models from "models/models";
import * as readlangLocalStorage from "readlangLocalStorage";
import * as dateUtils from "shared/dateUtils";
import { cn, sleep } from "shared/utils";
import * as svgUtils from "svgUtils";
import * as _ from "underscore";
import EmailReminderAndStreakRepairView from "views/EmailReminderAndStreakRepairView";

// Helper for fire icon
const svgFireIcon = (completed) => `
<svg class="fireIcon${completed ? " metGoal" : ""}" width="13" height="16" viewBox="0 0 13 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_5_21)">
<g clip-path="url(#clip1_5_21)">
<path d="M4.62254 0.168746C4.84888 -0.0593792 5.2 -0.0562542 5.42634 0.171871C6.22723 0.981246 6.97879 1.85312 7.68103 2.79687C8.00022 2.34687 8.36295 1.85625 8.75469 1.45625C8.98393 1.225 9.33795 1.225 9.56719 1.45937C10.5712 2.49062 11.4214 3.85312 12.0192 5.14687C12.6083 6.42187 13 7.725 13 8.64375C13 12.6312 10.104 16 6.5 16C2.85536 16 0 12.6281 0 8.64062C0 7.44062 0.516518 5.975 1.31741 4.525C2.12701 3.05312 3.27031 1.51875 4.62254 0.168746ZM6.54933 13C7.28348 13 7.93348 12.7812 8.54576 12.3437C9.76741 11.425 10.0953 9.5875 9.36116 8.14375C9.23058 7.8625 8.89687 7.84375 8.70826 8.08125L7.97701 8.99687C7.78549 9.23437 7.44018 9.22812 7.26027 8.98125C6.78147 8.325 5.92545 7.15312 5.43795 6.4875C5.25513 6.2375 4.90692 6.23437 4.72121 6.48437C3.7404 7.8125 3.2471 8.65 3.2471 9.59062C3.25 11.7312 4.7183 13 6.54933 13Z" fill="currentColor" />
</g>
</g>
<defs>
<clipPath id="clip0_5_21">
<rect width="13" height="16" fill="currentColor"/>
</clipPath>
<clipPath id="clip1_5_21">
<rect width="13" height="16" fill="currentColor"/>
</clipPath>
</defs>
</svg>
`;

interface Props {
  model: any; // StreakModel
  hideUponCompletion?: boolean;
  tooltipParentElement?: Element;
}

interface State {
  fadeOut: boolean;
}

class StreakView extends preact.Component<Props, State> {
  state: State = { fadeOut: false };
  color: [number, number, number];
  hideUponCompletion: boolean;
  elementRef: preact.Ref<HTMLDivElement>;

  constructor(props: Props) {
    super(props);
    this.hideUponCompletion = props.hideUponCompletion ?? false;
    this.elementRef = preact.createRef();
    models.streak.fetch();
  }

  componentDidMount() {
    // Listen for changes to the model and force update
    this.props.model.on("change", this.handleModelChange);
    models.storedNotifications?.on("change", this.handleModelChange);
  }

  componentWillUnmount() {
    this.props.model.off("change", this.handleModelChange);
    models.storedNotifications?.off("change", this.handleModelChange);
  }

  handleModelChange = () => {
    this.forceUpdate();
  };

  async componentDidUpdate() {
    const todayStats = this.props.model.get("todayStats");
    if (todayStats?.goalMet && this.hideUponCompletion) {
      await sleep(5000);
      this.setState({ fadeOut: true });
    }
    this.autoShowTooltipIfAppropriate();
  }

  async autoShowTooltipIfAppropriate() {
    const repairableDays = models.streak.get("repairableDays");
    if (repairableDays === undefined || repairableDays < 4) return;
    if (await this.haveAutoShownTooltipTodayAndUpdate()) return;
    this.showTooltip(true);
  }

  async haveAutoShownTooltipTodayAndUpdate() {
    const todayDate = new Date();
    const todayDayString = dateUtils.serializeDay(
      dateUtils.dateToDay(todayDate, dateUtils.getTimezoneOffset(todayDate))
    );
    const lastAutoShowTooltipDay = await new Promise((resolve) =>
      readlangLocalStorage.getItem("lastAutoShowTooltipDay", resolve)
    );
    await new Promise((resolve: any) =>
      readlangLocalStorage.setItem(
        "lastAutoShowTooltipDay",
        todayDayString,
        resolve
      )
    );
    if (!lastAutoShowTooltipDay) return false;
    if (lastAutoShowTooltipDay === todayDayString) return true;
    return false;
  }

  showTooltip = (autoShow = false) => {
    const tooltipElement = this.renderTooltip();
    const emailReminderOptInView = new EmailReminderAndStreakRepairView({
      models,
      el: tooltipElement.querySelector(".optIn"),
      autoShow,
    });

    if (this.props.tooltipParentElement) {
      this.props.tooltipParentElement.append(tooltipElement);
    } else {
      document.body.append(tooltipElement);
    }

    modalDialog.show(tooltipElement, {
      center: false,
      closeButton: false,
      title: "Streak Info",
      cancelCallback: () => {
        tooltipElement.remove();
        emailReminderOptInView.remove();
      },
    });
  };

  renderTooltip() {
    const todayStats = this.props.model.get("todayStats");
    const metGoalToday = todayStats.fractionOfGoalMet >= 1;
    const lastNotification = _.last(
      models.storedNotifications?.get("notifications") ?? []
    );
    const element = document.createElement("div");
    element.className = "streakTooltip";
    element.innerHTML = `
      <h4>${this.props.model.get("days")} Day Streak</h4>
      <p>${metGoalToday ? "Today's goal met!" : "Continue reading or practicing to meet today's goal!"}</p>
      <ul>
        <li>Words read: ${todayStats.wordsRead}</li>
        <li>Words practiced: ${todayStats.wordsPracticed}</li>
      </ul>
      <div class="optIn"></div>
      ${
        lastNotification
          ? `<div class="redDotNotice">
              <div class="dismissMessageButton icon-remove"></div>
              <p><span class="redDot"></span>${lastNotification.body}</p>
            </div>`
          : ""
      }
    `;
    const dismissBtn = element.querySelector(".dismissMessageButton");
    if (dismissBtn) {
      dismissBtn.addEventListener("click", async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (
          confirm(
            "This message and the red dot will disappear automatically once you reach your daily goal. Are you sure you want to delete it now?"
          )
        ) {
          await models.storedNotifications.deleteAllStoredNotifications();
          modalDialog.cancel();
        }
      });
    }
    return element;
  }

  handleClick = (event) => {
    event.preventDefault();
    this.showTooltip();
  };

  render() {
    if (!this.props.model.has("days")) return null;

    const todayStats = this.props.model.get("todayStats");
    const showRedDot =
      models.storedNotifications?.has("notifications") &&
      models.storedNotifications.get("notifications").length > 0;
    const outerWidth = 16;
    const svgArc = todayStats.goalMet ? (
      <div
        style={{
          display: "block",
          width: outerWidth * 2,
          height: outerWidth * 2,
        }}
      ></div>
    ) : (
      preact.h("div", {
        dangerouslySetInnerHTML: {
          __html: svgUtils.renderProgressRing(
            13.7,
            outerWidth,
            Math.min(1, todayStats.fractionOfGoalMet) * 360
          ),
        },
      })
    );
    return (
      <div
        class={cn({
          streakView: true,
          metGoal: todayStats.goalMet,
          fadeOut: this.state.fadeOut,
        })}
        onClick={this.handleClick}
        style={
          this.state.fadeOut
            ? {
                opacity: 0,
                width: 0,
                margin: 0,
                transition: "opacity 0.5s, width 0.5s, margin 0.5s",
              }
            : {}
        }
        ref={this.elementRef}
      >
        <div class="image">
          {svgArc}
          <span
            dangerouslySetInnerHTML={{
              __html: svgFireIcon(todayStats.goalMet),
            }}
          />
          {showRedDot ? <div class="redDot"></div> : null}
        </div>
        <div class={cn({ days: true, includesToday: todayStats.goalMet })}>
          {this.props.model.get("days")}
        </div>
      </div>
    );
  }
}

export default StreakView;
